import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue'
import { computed, reactive, Ref, ref, watch } from 'vue';
import { GradientStopObject, useColor } from '@/composables/color';
import { useClickOutside } from '@/composables/clickOutside';
import CButton from '@/components/shared/CButton';
import FontSelector from './FontSelector.vue';
import ColorSelector from './ColorSelector.vue';
import CInput, { type InputValue } from '@/components/shared/CInput';
import DraggableList from './DraggableList.vue';
import CPreview from '../shared/CPreview.vue';
import CFileInput, { type CFileInputRef }  from '../shared/CFileInput';
import CSelect, { type CSelectItem, valueReducer } from '@/components/shared/CSelect';
import type { SortableEvent } from 'sortablejs';
import type { ColorObject, GradientObject, LibraryDetail, LibraryDetailElement } from '@/typings/libraries';
import type { KeyValueDto } from '@/typings/keyValueDto';
import type { ElementMapOption } from './types';
import type { DynamicImage, FontColorOption } from '@/typings/customilyElements/canvasElements';
import { LibraryTypeEnum } from '@/utils/enums/libraryType.enum';
import { useFonts } from '@/composables/fonts';
import { getLibraryDetails, getLibraryElementDetails } from '@/api/libraries';
import fontManagerService from '@/utils/fontManagerService';
import { IMAGE_FILE_EXTENSIONS, VECTOR_FILE_EXTENSIONS } from '@/utils/pathUtils';


export default /*#__PURE__*/_defineComponent({
  __name: 'ElementLibrarySelector',
  props: {
    libraryId: null,
    libraryType: null,
    elementList: null,
    selectedPosition: null,
    cmyk: { type: Boolean },
    disabled: { type: Boolean },
    readonly: { type: Boolean },
    enablePalette: { type: Boolean },
    showMenuTypes: { type: Boolean },
    hideLibrarySelection: { type: Boolean },
    mixedLibrary: { type: Boolean },
    mixedElementList: { type: Boolean },
    elementListPosition: { default: 'unset' },
    readonlyOptions: { type: Boolean },
    externalImagePicker: { type: Boolean },
    libraryPickTypes: null,
    optionWording: null
  },
  emits: ["pick-library", "remove-library", "remove-map-option", "load-option", "option-selected", "options-reordered", "files-uploaded", "open-image-picker", "bulk-change-lib-option", "preview-selection", "library-loaded"],
  setup(__props: any, { emit }: { emit: ({
    (e: 'pick-library', libraryType?: LibraryTypeEnum): void;
    (e: 'remove-library'): void;
    (e: 'remove-map-option', position: number): void;
    (e: 'load-option', opt: number, value?: ElementMapOption): void;
    (e: 'option-selected', value: ElementMapOption, opt: number): void;
    (e: 'options-reordered', value: KeyValueDto<number, ElementMapOption>[]): void;
    (e: 'files-uploaded', value: File[], position?: number): void;
    (e: 'open-image-picker', position?: number): void;
    (e: 'bulk-change-lib-option', value: number): void;
    (e: 'preview-selection', value: ElementMapOption | undefined): void;
    (e: 'library-loaded', value: LibraryDetail): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { libraryId?: number, libraryType: number, elementList: Map<number, ElementMapOption>, selectedPosition?: number, cmyk?: boolean, disabled?: boolean, readonly?: boolean, enablePalette?: boolean, showMenuTypes?: boolean, hideLibrarySelection?: boolean, mixedLibrary?: boolean, mixedElementList?: boolean, elementListPosition: 'unset' | number, readonlyOptions?: boolean, externalImagePicker?: boolean, libraryPickTypes?: LibraryTypeEnum[], optionWording?: string };

_useCssVars((_vm, _setup) => ({
  "0a9ac196": (_vm.elementListPosition)
}))

const LIBRARY_PICK_LABELS: Record<number, string> = {
    [LibraryTypeEnum.Color]: 'Color Library',
    [LibraryTypeEnum.Font]: 'Font Library',
    [LibraryTypeEnum.Vector]: 'Vector Library',
    [LibraryTypeEnum.Image]: 'Image Library'
};

const libSearchOptions: CSelectItem<boolean>[] = [
    {
        label: 'Name',
        value: true
    },
    {
        label: 'Option',
        value: false
    }
];





const params = computed(() => ({
    ...libraryParams.value,
    wording: props.optionWording || libraryParams.value.wording
}));

const libraryParams = computed(() => {
    switch (props.libraryType) {
        case LibraryTypeEnum.Color:
            return {
                isColorLib: true,
                wording: 'color'
            };
        case LibraryTypeEnum.Vector:
            return {
                isVectorLib: true,
                wording: 'vector',
                fileTypes: [...VECTOR_FILE_EXTENSIONS].join(','),
                editIcon: 'arrow-up-from-bracket'
            };
        case LibraryTypeEnum.Image:
            return {
                isImageLib: true,
                wording: 'image',
                fileTypes: [...IMAGE_FILE_EXTENSIONS].join(','),
                editIcon: 'arrow-up-from-bracket'
            };
        default:
            return {
                isFontLib: true,
                wording: 'font'
            };
    }
});

const fonts = useFonts();

const selectedFont: Ref<CSelectItem<string> | undefined> = ref();
const optionsMap: Ref<KeyValueDto<number, ElementMapOption>[]> = ref([]);

const selectionMode = reactive({
    open: false,
    positionToEdit: undefined as number | undefined
});

const { hexToHexa, getGradientCSS } = useColor();

const getElementName = (value: ElementMapOption) => {
    let name = '';

    if (!value) return name;

    if (params.value.isFontLib) {
        const font = fontManagerService.getFontFromPath(value as string)!;
        name = fonts.generateFontName(font);
    } else if (params.value.isColorLib) {
        selectionMode.open = true;
        const color = value as ColorObject | GradientObject;
        if ('colorStops' in color) {
            name = color.type === 'linear' ? 'Linear Gradient' : 'Radial Gradient';
        } else if ('colors' in color) {
            name = 'Alternating Colors';
        } else {
            name = hexToHexa(color.hex, color.alpha);
        }
    } else if (params.value.isImageLib) {
        name = (value as DynamicImage).fileName || '';
    }
    return name;
};

const getElementPreview = (value: ElementMapOption) => {
    if (params.value.isImageLib) {
        return (value as DynamicImage).path;
    }
    return value;
};

const getColorElement = (color: unknown): Record<string, string> => {
    if (!color || typeof color !== 'object') return {};
    if ('value' in color) {
        color = color.value;
    }
    const newColor = color as FontColorOption;
    if ('colorStops' in newColor) {
        return getGradientCSS(newColor.colorStops as Pick<GradientStopObject, 'hex' | 'offset'>[], newColor.angle || 0);
    } else if ('colors' in newColor) {
        return {
            backgroundColor: hexToHexa(newColor.colors[0].hex, newColor.colors[0].alpha)
        };
    }
    return {
        backgroundColor: hexToHexa(newColor.hex, newColor.alpha)
    };
};

watch(
    () => props.elementList,
    (elementList) => {
        optionsMap.value = Array.from(elementList).map(([_key, value]) => {
            return {
                id: _key,
                value,
                name: getElementName(value)
            };
        });

        if (optionsMap.value.length != 1) return;

        selectionMode.open = false;
        const option = optionsMap.value[0];

        if (params.value.isFontLib) {
            selectionMode.positionToEdit = option.id;
            selectedFont.value = {
                label: option.name!,
                value: option.value! as string
            };
        }
    },
    { deep: true, immediate: true }
);

const colorElementToEdit = computed(() =>
    params.value.isColorLib && selectionMode.positionToEdit && props.elementList
        ? props.elementList.get(selectionMode.positionToEdit) as ColorObject | GradientObject
        : undefined
);

// Without libraryPickTypes the type stays implicit, so the payload is left out and every current
//  consumer keeps receiving nothing
const libraryPickOptions = computed(() =>
    props.libraryPickTypes?.length
        ? props.libraryPickTypes.map((type) => ({ type, text: LIBRARY_PICK_LABELS[type] }))
        : [{ type: undefined, text: 'Pick Library' }]
);

const displayFontSelector = computed(() => params.value.isFontLib && (selectionMode.open || optionsMap.value.length == 1));

const displayOptionMapList = computed(() =>
    optionsMap.value.length > 1 || selectionMode.open || !params.value.isFontLib
);

const library: Ref<LibraryDetail | undefined> = ref();
const cachedLibs: Map<number, LibraryDetail> = new Map();
const selectedLibCategory: Ref<CSelectItem<number> | undefined> = ref();
const libraryCategories: Ref<CSelectItem<number>[]> = ref([]);

const getLibrary = async (libraryId: number) => {
    const response = cachedLibs.get(libraryId) || (await getLibraryDetails(libraryId));
    library.value = { ...response };
    cachedLibs.set(libraryId, response);
    if (response.categories?.length) {
        library.value.elements = [];
        libraryCategories.value = response.categories.map<CSelectItem<number>>((category) => ({
            value: category.id!,
            label: category.name!
        }));
        selectedLibCategory.value = libraryCategories.value[0];
        await onCategorySelected();
    } else {
        libraryCategories.value = [];
        selectedLibCategory.value = undefined;
    }

    if (library.value.elements.length && props.selectedPosition == 1) {
        onLibraryElementSelected(library.value.elements[0]);
    }
    emit('library-loaded', library.value);
};

const onCategorySelected = async () => {
    await getLibraryElements(selectedLibCategory.value?.value)
}

const searchString = ref('');
const searchByName = ref(true);
const onElementSearch = async () => {
    if (!searchString.value?.toString().trim().length) {
        await onCategorySelected();
        return;
    }

    await getLibraryElements();
}

const resetSearch = async () => {
    searchString.value = '';
    await onCategorySelected();
}

const getLibraryElements = async (categoryId?: number) => {
    if (!library.value) return;

    let position: number | undefined;
    let searchValue: string | undefined = searchString.value;

    if (!searchByName.value && searchString.value.toString().length) {
        searchValue = undefined;
        position = Number(searchString.value);
    }

    const elements = await getLibraryElementDetails(
        library.value.id,
        library.value.type,
        categoryId,
        searchValue,
        position
    );
    library.value.elements = elements;
};

const onLibraryElementSelected = (element: LibraryDetailElement) => {
    const value: ElementMapOption = !params.value.isColorLib
        ? element.path!
        : {
            hex: element.hex || '',
            alpha: element.alpha || 1,
            cmyk: ''
        };
    changeOption(element.position, value);

    if (libraryCategories.value.length && element.categoryId != selectedLibCategory.value?.value) {
        selectedLibCategory.value = libraryCategories.value.find((category) => category.value == element.categoryId);
        onCategorySelected();
    }
};

const onRemoveLibrary = () => {
    libraryCategories.value = [];
    selectedLibCategory.value = undefined;
    emit('remove-library');
};

const openElementSelection = (position?: number) => {
    if (props.disabled || props.readonly || props.readonlyOptions) return;

    if (props.externalImagePicker && (params.value.isImageLib || params.value.isVectorLib)) {
        emit('open-image-picker', position);
        return;
    }

    selectionMode.open = true;
    selectionMode.positionToEdit = position;

    if (params.value.isImageLib ||params.value.isVectorLib) {
        fileInputRef.value?.openFileUploader();
        return;
    }

    clickOutsideActive.value = true;
};

const closeSelection = () => {
    if (!selectionMode.open) return;
    selectionMode.open = false;
    selectionMode.positionToEdit = undefined;
    clickOutsideActive.value = false;
    emit('preview-selection', undefined);
};

const pickerRef: Ref<HTMLElement | undefined> = ref();
const { clickOutsideActive } = useClickOutside(pickerRef, closeSelection, false);

const onElementSelected = (element?: ElementMapOption) => {
    if (props.mixedElementList && element) {
        emitOptionSelection(1, element)
        closeSelection();
        return;
    }

    if (optionsMap.value.length <= 1 && !selectionMode.open && selectionMode.positionToEdit !== undefined) {
        emitOptionSelection(1);
        return;
    }

    const lastPosition = optionsMap.value.at(-1)?.id || 0;
    const position = selectionMode.positionToEdit || lastPosition + 1;
    closeSelection();
    emitOptionSelection(position, element);
};

const emitOptionSelection = (position: number, value?: ElementMapOption) => {
    value ||= selectedFont.value?.value;
    if (!value) return;

    emit('option-selected', value, position);
};

const onMapOptionRemoved = (position: number, index: number) => {
    if (index == optionsMap.value.length - 1) {
        emit('remove-map-option', position);
        return;
    }

    optionsMap.value.splice(index, 1);
    emit('options-reordered', optionsMap.value);
};

const onOptionDragEnd = (event: SortableEvent) => {
    const { oldIndex = 0, newIndex = 0 } = event;

    if (oldIndex == newIndex) return;
    emit('options-reordered', optionsMap.value);
};

const generateSafeFontName = fontManagerService.generateSafeFontName;

watch(
    () => props.libraryId,
    (value, oldValue) => {
        if (value != library.value?.id) {
            library.value = undefined;
        }
        if (!value || value == oldValue) return;

        getLibrary(value);
    },
    { immediate: true }
);

const changeOption = async (position: InputValue, value?: ElementMapOption) => {
    emit('load-option', position as number, value);
};

const fileInputRef: Ref<CFileInputRef | undefined> = ref();
const onFileUpload = (files: File[]) => {
    if (!files.length) return;
    emit('files-uploaded', files, selectionMode.positionToEdit);
};

return { __sfc: true,LIBRARY_PICK_LABELS, libSearchOptions, props, emit, params, libraryParams, fonts, selectedFont, optionsMap, selectionMode, hexToHexa, getGradientCSS, getElementName, getElementPreview, getColorElement, colorElementToEdit, libraryPickOptions, displayFontSelector, displayOptionMapList, library, cachedLibs, selectedLibCategory, libraryCategories, getLibrary, onCategorySelected, searchString, searchByName, onElementSearch, resetSearch, getLibraryElements, onLibraryElementSelected, onRemoveLibrary, openElementSelection, closeSelection, pickerRef, clickOutsideActive, onElementSelected, emitOptionSelection, onMapOptionRemoved, onOptionDragEnd, generateSafeFontName, changeOption, fileInputRef, onFileUpload, CButton, FontSelector, ColorSelector, CInput, DraggableList, CPreview, CFileInput, CSelect, valueReducer }
}

})